import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import api, { downloadFile, getMediaBlobUrl } from '../../utils/api';
import { 
  Image, Video, Download, Folder, ArrowLeft, Eye, X, 
  ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Loader2, Play
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import BackButton from '../../components/BackButton';

const Gallery = () => {
  const [albums, setAlbums] = useState([]);
  const [currentAlbum, setCurrentAlbum] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxItem, setLightboxItem] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxLoading, setLightboxLoading] = useState(false);
  const [lightboxBlobUrl, setLightboxBlobUrl] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  
  // Thumbnail blob URLs cache
  const [thumbnailUrls, setThumbnailUrls] = useState({});

  useEffect(() => { loadAlbums(); }, []);
  useEffect(() => { 
    if (currentAlbum) {
      loadItems(currentAlbum.id);
      setThumbnailUrls({});
    }
  }, [currentAlbum]);

  // Load thumbnails when items change
  useEffect(() => {
    const loadThumbnails = async () => {
      for (const item of items) {
        if (!thumbnailUrls[item.id] && item.type === 'image') {
          try {
            const blobUrl = await getMediaBlobUrl(item.file_url);
            if (blobUrl) {
              setThumbnailUrls(prev => ({ ...prev, [item.id]: blobUrl }));
            }
          } catch (error) {
            console.error('Failed to load thumbnail:', item.id);
          }
        }
      }
    };
    if (items.length > 0) {
      loadThumbnails();
    }
  }, [items]);

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      Object.values(thumbnailUrls).forEach(url => {
        if (url) window.URL.revokeObjectURL(url);
      });
      if (lightboxBlobUrl) window.URL.revokeObjectURL(lightboxBlobUrl);
    };
  }, []);

  const loadAlbums = async () => {
    try {
      setLoading(true);
      const response = await api.get('/gallery/albums');
      setAlbums(response.data);
    } catch (error) {
      toast.error('Failed to load albums');
    } finally {
      setLoading(false);
    }
  };

  const loadItems = async (albumId) => {
    try {
      setLoading(true);
      const response = await api.get(`/gallery/items/${albumId}`);
      setItems(response.data);
    } catch (error) {
      toast.error('Failed to load gallery items');
    } finally {
      setLoading(false);
    }
  };

  // Open lightbox viewer
  const openLightbox = async (item, index) => {
    setLightboxItem(item);
    setLightboxIndex(index);
    setLightboxOpen(true);
    setLightboxLoading(true);
    setZoomLevel(1);
    
    try {
      const blobUrl = await getMediaBlobUrl(item.file_url);
      setLightboxBlobUrl(blobUrl);
    } catch (error) {
      toast.error('Failed to load media');
    } finally {
      setLightboxLoading(false);
    }
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setLightboxItem(null);
    if (lightboxBlobUrl) {
      window.URL.revokeObjectURL(lightboxBlobUrl);
      setLightboxBlobUrl(null);
    }
    setZoomLevel(1);
  };

  const navigateLightbox = async (direction) => {
    const newIndex = direction === 'next' 
      ? (lightboxIndex + 1) % items.length 
      : (lightboxIndex - 1 + items.length) % items.length;
    
    const newItem = items[newIndex];
    setLightboxIndex(newIndex);
    setLightboxItem(newItem);
    setLightboxLoading(true);
    setZoomLevel(1);
    
    if (lightboxBlobUrl) {
      window.URL.revokeObjectURL(lightboxBlobUrl);
    }
    
    try {
      const blobUrl = await getMediaBlobUrl(newItem.file_url);
      setLightboxBlobUrl(blobUrl);
    } catch (error) {
      toast.error('Failed to load media');
    } finally {
      setLightboxLoading(false);
    }
  };

  const handleDownload = async (item) => {
    try {
      await downloadFile(item.file_url, item.caption);
      toast.success('Download started');
    } catch (error) {
      toast.error('Download failed. Please try again.');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric'
    });
  };

  // Album list view
  if (!currentAlbum) {
    return (
      <div className="space-y-6" data-testid="member-gallery-page">
        <BackButton to="/member" />
        <div>
          <h1 className="text-2xl font-serif font-bold text-slate-900">Gallery</h1>
          <p className="text-slate-600">Browse organization photos and videos</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
          </div>
        ) : albums.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {albums.map((album) => (
              <div 
                key={album.id} 
                className="bg-white border border-slate-200 rounded-xl p-5 group hover:border-orange-500 cursor-pointer transition-all duration-300 hover:shadow-lg"
                onClick={() => setCurrentAlbum(album)}
              >
                <div className="flex items-start mb-3">
                  <div className="bg-gradient-to-br from-orange-500 to-amber-600 p-3 rounded-xl shadow-lg">
                    <Folder className="w-6 h-6 text-white" />
                  </div>
                </div>
                <h3 className="font-semibold text-lg text-slate-800 mb-2 group-hover:text-orange-600 transition-colors">{album.name}</h3>
                {album.description && <p className="text-sm text-slate-600 mb-3 line-clamp-2">{album.description}</p>}
                <p className="text-xs text-slate-500">{formatDate(album.created_at)}</p>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-xl text-center py-16">
            <Folder className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <p className="text-slate-600 text-lg">No albums available</p>
            <p className="text-sm text-slate-500 mt-2">Check back later for photos and videos</p>
          </div>
        )}
      </div>
    );
  }

  // Album items view with professional gallery
  return (
    <div className="space-y-6" data-testid="gallery-items-view">
      <div className="flex items-center space-x-4">
        <Button variant="ghost" onClick={() => { setCurrentAlbum(null); setItems([]); setThumbnailUrls({}); }} className="text-slate-600 hover:text-slate-800">
          <ArrowLeft className="w-4 h-4 mr-2" />Back to Albums
        </Button>
      </div>
      
      <div>
        <h1 className="text-2xl font-serif font-bold text-slate-900">{currentAlbum.name}</h1>
        <p className="text-slate-600">{currentAlbum.description || 'Browse photos and videos'}</p>
        <p className="text-xs text-slate-500 mt-1">{items.length} items • Click to preview, then download</p>
      </div>

      {/* Professional Gallery Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
        </div>
      ) : items.length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {items.map((item, index) => (
            <div 
              key={item.id} 
              className="group relative bg-white rounded-xl overflow-hidden border border-slate-200 hover:border-orange-500 transition-all duration-300 hover:shadow-lg cursor-pointer"
              onClick={() => openLightbox(item, index)}
              onContextMenu={(e) => e.preventDefault()}
              data-testid={`gallery-item-${item.id}`}
            >
              {/* Thumbnail */}
              <div className="aspect-square relative overflow-hidden bg-slate-100">
                {item.type === 'video' ? (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200">
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="bg-orange-500/90 backdrop-blur-sm rounded-full p-4 shadow-lg">
                        <Play className="w-8 h-8 text-white" fill="white" />
                      </div>
                    </div>
                    <Video className="w-12 h-12 text-slate-300 absolute bottom-2 right-2" />
                  </div>
                ) : (
                  <>
                    {thumbnailUrls[item.id] ? (
                      <img 
                        src={thumbnailUrls[item.id]} 
                        alt={item.caption} 
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        draggable="false"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                      </div>
                    )}
                  </>
                )}
                
                {/* Hover Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <div className="flex items-center space-x-2">
                    <div className="bg-white/20 backdrop-blur-sm rounded-full p-3 hover:bg-white/30 transition-colors">
                      <Eye className="w-5 h-5 text-white" />
                    </div>
                  </div>
                  <span className="absolute bottom-3 left-3 text-white text-sm font-medium">Click to view</span>
                </div>
                
                {/* Media Type Badge */}
                <div className="absolute top-2 left-2">
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${item.type === 'video' ? 'bg-purple-500/90 text-white' : 'bg-blue-500/90 text-white'}`}>
                    {item.type === 'video' ? 'Video' : 'Image'}
                  </span>
                </div>
              </div>
              
              {/* Item Info */}
              <div className="p-3">
                <p className="text-sm text-slate-800 truncate font-medium">{item.caption || 'Untitled'}</p>
                <p className="text-xs text-slate-500 mt-1">{formatDate(item.created_at)}</p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl text-center py-16">
          <Image className="w-16 h-16 mx-auto mb-4 text-slate-300" />
          <p className="text-slate-600 text-lg">No media in this album</p>
          <p className="text-sm text-slate-500 mt-2">Check back later for updates</p>
        </div>
      )}

      {/* Lightbox Modal */}
      {lightboxOpen && lightboxItem && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={closeLightbox}
        >
          {/* Close Button */}
          <button 
            onClick={closeLightbox}
            className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors z-50"
          >
            <X className="w-6 h-6 text-white" />
          </button>
          
          {/* Navigation Arrows */}
          {items.length > 1 && (
            <>
              <button 
                onClick={(e) => { e.stopPropagation(); navigateLightbox('prev'); }}
                className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors z-50"
              >
                <ChevronLeft className="w-8 h-8 text-white" />
              </button>
              <button 
                onClick={(e) => { e.stopPropagation(); navigateLightbox('next'); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors z-50"
              >
                <ChevronRight className="w-8 h-8 text-white" />
              </button>
            </>
          )}
          
          {/* Media Content */}
          <div 
            className="max-w-[90vw] max-h-[80vh] relative"
            onClick={(e) => e.stopPropagation()}
          >
            {lightboxLoading ? (
              <div className="flex items-center justify-center w-64 h-64">
                <Loader2 className="w-12 h-12 animate-spin text-orange-500" />
              </div>
            ) : lightboxBlobUrl ? (
              lightboxItem.type === 'video' ? (
                <video 
                  src={lightboxBlobUrl} 
                  controls 
                  autoPlay
                  className="max-w-full max-h-[80vh] rounded-lg"
                  onContextMenu={(e) => e.preventDefault()}
                />
              ) : (
                <img 
                  src={lightboxBlobUrl} 
                  alt={lightboxItem.caption}
                  className="max-w-full max-h-[80vh] object-contain rounded-lg transition-transform duration-300"
                  style={{ transform: `scale(${zoomLevel})` }}
                  draggable="false"
                  onContextMenu={(e) => e.preventDefault()}
                />
              )
            ) : (
              <div className="flex items-center justify-center w-64 h-64 bg-slate-800 rounded-lg">
                <p className="text-slate-400">Failed to load media</p>
              </div>
            )}
          </div>
          
          {/* Bottom Controls */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-white font-semibold text-lg">{lightboxItem.caption || 'Untitled'}</h3>
                  <p className="text-slate-400 text-sm">{lightboxIndex + 1} of {items.length} • {formatDate(lightboxItem.created_at)}</p>
                </div>
                <div className="flex items-center space-x-3">
                  {lightboxItem.type === 'image' && (
                    <>
                      <button 
                        onClick={() => setZoomLevel(Math.max(0.5, zoomLevel - 0.25))}
                        className="p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
                        title="Zoom Out"
                      >
                        <ZoomOut className="w-5 h-5 text-white" />
                      </button>
                      <span className="text-white text-sm">{Math.round(zoomLevel * 100)}%</span>
                      <button 
                        onClick={() => setZoomLevel(Math.min(3, zoomLevel + 0.25))}
                        className="p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
                        title="Zoom In"
                      >
                        <ZoomIn className="w-5 h-5 text-white" />
                      </button>
                    </>
                  )}
                  <Button 
                    onClick={() => handleDownload(lightboxItem)}
                    className="bg-orange-600 hover:bg-orange-700"
                    data-testid="lightbox-download-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;
